import { Asset, GSplatData, GSplatResource, Vec3, type AppBase, type Model } from 'playcanvas';

import { ElementType } from './element';
import { Mesh } from './mesh';
import { buildModelFromObj } from './obj-importer';
import { parseObj } from './obj-parser';
import { Scene } from './scene';
import { Splat } from './splat';
import { State } from './splat-state';
import { cloneTypedArray } from './typed-array-utils';

export type DuplicateMode = 'true' | 'instance';

const getUniqueName = (scene: Scene, type: ElementType, desired: string): string => {
    const existing = new Set<string>();
    for (const el of scene.getElementsByType(type)) {
        const n = String((el as any).name ?? '').trim();
        if (n) existing.add(n);
    }

    if (!existing.has(desired)) return desired;

    for (let i = 2; i < 10_000; i++) {
        const candidate = `${desired} ${i}`;
        if (!existing.has(candidate)) return candidate;
    }

    return `${desired} ${Date.now()}`;
};

const insertBeforeExtension = (filename: string, insert: string): string => {
    const input = String(filename ?? '');
    const sepIdx = Math.max(input.lastIndexOf('/'), input.lastIndexOf('\\'));
    const prefix = sepIdx >= 0 ? input.slice(0, sepIdx + 1) : '';
    const nameOnly = sepIdx >= 0 ? input.slice(sepIdx + 1) : input;

    const dotIdx = nameOnly.lastIndexOf('.');
    if (dotIdx <= 0) {
        return `${prefix}${nameOnly}${insert}`;
    }

    return `${prefix}${nameOnly.slice(0, dotIdx)}${insert}${nameOnly.slice(dotIdx)}`;
};

const ensureHasExtension = (filename: string, defaultExt: string): string => {
    const input = String(filename ?? '');
    if (/\.[^./\\]+$/.test(input)) return input;
    return `${input}${defaultExt}`;
};

const cloneFileMetadata = (file: File): File => {
    return new File([file], file.name, {
        type: file.type,
        lastModified: file.lastModified
    });
};

const guessMimeFromFilename = (filename: string): string => {
    const lower = (filename ?? '').toLowerCase();
    if (lower.endsWith('.glb')) return 'model/gltf-binary';
    if (lower.endsWith('.gltf')) return 'model/gltf+json';
    if (lower.endsWith('.obj')) return 'text/plain';
    return 'application/octet-stream';
};

const getMeshSourceBlobOrNull = (mesh: Mesh): Blob | null => {
    const anyMesh: any = mesh as any;
    return (anyMesh?._sourceMeshFile as Blob) ?? null;
};

const getMeshSourceNameOrFallback = (mesh: Mesh): string => {
    const anyMesh: any = mesh as any;
    return String(anyMesh?._sourceMeshName ?? mesh.filename ?? mesh.name ?? 'mesh');
};

const fetchArrayBufferOrNull = async (url: string): Promise<ArrayBuffer | null> => {
    try {
        const res = await fetch(url);
        if (!res.ok) return null;
        return await res.arrayBuffer();
    } catch {
        return null;
    }
};

const loadAsset = async (app: AppBase, asset: Asset): Promise<void> => {
    await new Promise<void>((resolve, reject) => {
        asset.once('load', () => resolve());
        asset.once('error', (err: any) => reject(new Error(typeof err === 'string' ? err : 'asset load error')));
        app.assets.add(asset);
        app.assets.load(asset);
    });
};

const cloneSplatDataDeep = (src: GSplatData): GSplatData => {
    const srcAny: any = src as any;
    const elements = (srcAny?.elements ?? srcAny?._elements ?? null) as any[] | null;

    const cloneElement = (el: any) => {
        const props = (el?.properties ?? []) as any[];
        return {
            name: String(el?.name ?? 'vertex'),
            count: Number(el?.count ?? src.numSplats),
            properties: props.map((p) => {
                const storage = p?.storage as ArrayBufferView;
                if (!storage || typeof (storage as any).buffer === 'undefined') {
                    return { ...p };
                }

                return {
                    type: p.type,
                    name: p.name,
                    storage: cloneTypedArray(storage),
                    byteSize: p.byteSize
                };
            })
        };
    };

    if (Array.isArray(elements) && elements.length) {
        return new GSplatData(elements.map(cloneElement));
    }

    const vertex = (src as any).getElement?.('vertex');
    if (!vertex) {
        throw new Error('Cannot duplicate splat: missing vertex element');
    }

    return new GSplatData([cloneElement(vertex)]);
};

export const duplicateSplat = (splat: Splat, scene: Scene, mode: DuplicateMode): Promise<Splat> => {
    if (mode === 'instance') {
        throw new Error('Splat Instance is disabled: per-instance selection/deletion state would leak across instances');
    }

    const clonedData = typeof (splat as any).cloneSplatData === 'function' ?
        (splat as any).cloneSplatData() as GSplatData :
        cloneSplatDataDeep(splat.splatData);

    const state = clonedData.getProp('state') as Uint8Array;
    if (state) {
        for (let i = 0; i < state.length; i++) {
            state[i] &= ~State.selected;
        }
    }

    const srcFilename = String((splat.asset?.file as any)?.filename ?? splat.filename ?? splat.name ?? 'splat');
    const assetFilename = insertBeforeExtension(srcFilename, ' Copy');

    const asset = new Asset(assetFilename, 'gsplat', { url: `duplicate-gsplat-${Date.now()}`, filename: assetFilename });
    scene.app.assets.add(asset);
    asset.resource = new GSplatResource(scene.app.graphicsDevice, clonedData);

    const dup = new Splat(asset, new Vec3(0, 0, 0));
    dup.name = getUniqueName(scene, ElementType.splat, `${splat.name} Copy`);
    dup.move(
        splat.entity.getLocalPosition().clone(),
        splat.entity.getLocalRotation().clone(),
        splat.entity.getLocalScale().clone()
    );
    dup.visible = splat.visible;

    try {
        dup.pivotOffset.copy(splat.pivotOffset);
    } catch {
        // ignore
    }

    try {
        dup.setImportNormalization(splat.importNormalizationRotation);
    } catch {
        // ignore
    }

    dup.tintClr = splat.tintClr;
    dup.temperature = splat.temperature;
    dup.saturation = splat.saturation;
    dup.brightness = splat.brightness;
    dup.blackPoint = splat.blackPoint;
    dup.whitePoint = splat.whitePoint;
    dup.transparency = splat.transparency;

    return Promise.resolve(dup);
};

const cloneMeshAssetTrue = async (mesh: Mesh, scene: Scene): Promise<{ asset: Asset; sourceBytes: ArrayBuffer; sourceName: string }> => {
    const srcNameRaw = getMeshSourceNameOrFallback(mesh);
    const srcName = ensureHasExtension(srcNameRaw, '.glb');
    const lower = srcName.toLowerCase();

    if (lower.endsWith('.obj')) {
        const blob = getMeshSourceBlobOrNull(mesh);
        if (!blob) {
            throw new Error('Cannot duplicate OBJ mesh: source file not available');
        }

        const bytes = await blob.arrayBuffer();
        const text = new TextDecoder().decode(new Uint8Array(bytes));
        const parsed = parseObj(text);
        const model: Model = buildModelFromObj(scene.app, parsed);

        const assetFilename = insertBeforeExtension(ensureHasExtension(srcName, '.obj'), ' Copy');
        const asset = new Asset(assetFilename, 'model', { filename: assetFilename });
        asset.resource = model as any;
        scene.app.assets.add(asset);
        return { asset, sourceBytes: bytes, sourceName: ensureHasExtension(srcName, '.obj') };
    }

    const blob = getMeshSourceBlobOrNull(mesh);
    let bytes: ArrayBuffer | null = blob ? await blob.arrayBuffer() : null;

    if (!bytes) {
        const url = (mesh.asset?.file as any)?.url as string | undefined;
        if (typeof url === 'string' && url) {
            bytes = await fetchArrayBufferOrNull(url);
        }
    }

    if (!bytes) {
        throw new Error('Cannot duplicate mesh: source bytes unavailable (this mesh was imported without stored source data)');
    }

    const mime = guessMimeFromFilename(srcName);
    const url = URL.createObjectURL(new Blob([bytes], { type: mime }));

    try {
        const assetFilename = insertBeforeExtension(srcName, ' Copy');
        const assetType = mesh.asset?.type ?? 'container';
        const asset = new Asset(assetFilename, assetType as any, { url, filename: assetFilename });
        await loadAsset(scene.app, asset);
        return { asset, sourceBytes: bytes, sourceName: srcName };
    } finally {
        try {
            URL.revokeObjectURL(url);
        } catch {
            // ignore
        }
    }
};

const copyMeshSourceAndTextureMetadata = (src: Mesh, dst: Mesh, options: { mode: DuplicateMode; sourceBytes?: ArrayBuffer; sourceName?: string }) => {
    const anySrc: any = src as any;
    const anyDst: any = dst as any;

    const srcNameRaw = options.sourceName ?? anySrc._sourceMeshName ?? src.filename ?? src.name ?? 'mesh.glb';
    const srcName = ensureHasExtension(srcNameRaw, '.glb');
    const nameWithMode = insertBeforeExtension(srcName, options.mode === 'instance' ? ' Instance' : ' Copy');

    if (options.sourceBytes) {
        const mime = guessMimeFromFilename(srcName);
        anyDst._sourceMeshFile = new Blob([options.sourceBytes], { type: mime });
        anyDst._sourceMeshName = nameWithMode;
    } else {
        if (anySrc._sourceMeshFile) anyDst._sourceMeshFile = anySrc._sourceMeshFile;
        if (anySrc._sourceMeshName) {
            anyDst._sourceMeshName = options.mode === 'instance' ? nameWithMode : insertBeforeExtension(anySrc._sourceMeshName, ' Copy');
        } else {
            anyDst._sourceMeshName = nameWithMode;
        }
    }

    if (anySrc._sourceTextureFile) anyDst._sourceTextureFile = anySrc._sourceTextureFile;
    if (anySrc._sourceTextureName) anyDst._sourceTextureName = anySrc._sourceTextureName;
    if (anySrc._sourceVideoFile instanceof File) {
        anyDst._sourceVideoFile = cloneFileMetadata(anySrc._sourceVideoFile);
    }
};

const copyMeshSettings = (src: Mesh, dst: Mesh, scene: Scene) => {
    dst._name = getUniqueName(scene, ElementType.model, `${src.name} Copy`);
    dst.entity.name = dst._name;

    dst.entity.setLocalPosition(src.entity.getLocalPosition().clone());
    dst.entity.setLocalRotation(src.entity.getLocalRotation().clone());
    dst.entity.setLocalScale(src.entity.getLocalScale().clone());

    dst._visible = src.visible;
    dst.entity.enabled = src.visible;
    dst._tintClr.copy(src.tintClr);
    dst._transparency = src.transparency;

    dst.renderVisible = src.renderVisible;
    dst.collisionMode = src.collisionMode;
    dst.collisionShape = src.collisionShape;
    dst.debugCollider = src.debugCollider;
    dst.emissiveUnlitEnabled = src.emissiveUnlitEnabled;
    dst.useAlpha = src.useAlpha;
    dst.billboard = src.billboard;
    dst.videoPlaybackMode = src.videoPlaybackMode;

    try {
        dst.pivotOffset.copy(src.pivotOffset);
    } catch {
        // ignore
    }
};

export const duplicateMesh = async (mesh: Mesh, scene: Scene, mode: DuplicateMode): Promise<Mesh> => {
    if (mode === 'instance') {
        const inst = new Mesh(mesh.asset);
        copyMeshSettings(mesh, inst, scene);
        inst._name = getUniqueName(scene, ElementType.model, `${mesh.name} Instance`);
        inst.entity.name = inst._name;
        copyMeshSourceAndTextureMetadata(mesh, inst, { mode: 'instance' });
        return inst;
    }

    const { asset, sourceBytes, sourceName } = await cloneMeshAssetTrue(mesh, scene);
    const dup = new Mesh(asset);
    copyMeshSettings(mesh, dup, scene);
    copyMeshSourceAndTextureMetadata(mesh, dup, { mode: 'true', sourceBytes, sourceName });
    return dup;
};
